import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBrand } from "../context/BrandContext";
import { ASSIGNABLE_ROLES, MULTI_BRAND_ROLES, ROLE, roleLabel } from "../lib/roles";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Plus, X, Key, Check, Trash } from "@phosphor-icons/react";

const inp = "w-full border border-mist rounded-sm px-3 py-2 bg-white text-base md:text-sm focus:outline-none focus:border-sage-dark";

export default function Users() {
    const { user: me } = useAuth();
    const { brands } = useBrand();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [resetFor, setResetFor] = useState(null);
    const [deleteFor, setDeleteFor] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/users");
            setUsers(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const updateUser = async (uid, patch) => {
        try {
            await api.put(`/users/${uid}`, patch);
            await load();
            toast.success("User updated");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Failed to update");
        }
    };

    const brandLabel = (id) => brands.find((b) => b.id === id)?.name || id;

    return (
        <div className="animate-fade-up max-w-3xl w-full font-sans">
            <Link to="/settings" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-sage-dark">
                <ArrowLeft size={14} weight="light" /> Back to Settings
            </Link>
            <div className="flex items-end justify-between gap-4 mt-4">
                <div>
                    <div className="eyebrow">Settings · Users & Access</div>
                    <h1 className="font-serif text-4xl md:text-5xl mt-1 tracking-tight">Users & Access</h1>
                </div>
                <button
                    onClick={() => setShowAdd(true)}
                    data-testid="users-add-button"
                    className="inline-flex items-center gap-1 bg-sage-dark text-white px-3 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep"
                >
                    <Plus size={14} weight="bold" /> Add User
                </button>
            </div>

            {loading ? (
                <div className="text-sm text-neutral-400 py-16 text-center">Loading…</div>
            ) : (
                <ul className="mt-6 border border-mist rounded-sm bg-white divide-y divide-mist">
                    {users.map((u) => {
                        const isMe = u.id === me?.id;
                        const isOwnerRow = u.role === ROLE.OWNER;
                        const assignedBrands = u.brands || [u.brand];
                        return (
                            <li key={u.id} className="p-4 space-y-3" data-testid={`user-row-${u.email}`}>
                                <div className="flex flex-col gap-1">
                                    <div className="font-serif text-xl flex items-center gap-2 flex-wrap">
                                        {u.name}
                                        {isMe && <span className="eyebrow text-[9px]">You</span>}
                                        {u.active === false && (
                                            <span className="text-[9px] uppercase tracking-widest text-neutral-400 border border-mist px-1.5 py-0.5 rounded-full">
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-neutral-500 font-mono break-all">{u.email}</div>
                                    <div className="text-[10px] uppercase tracking-widest text-neutral-500 flex flex-wrap gap-x-4 gap-y-1 mt-1">
                                        <span>
                                            <span className="text-neutral-400">Role · </span>
                                            <span className="text-ink">{roleLabel(u.role)}</span>
                                        </span>
                                        <span>
                                            <span className="text-neutral-400">Access · </span>
                                            <span className="text-ink normal-case tracking-normal">
                                                {isOwnerRow
                                                    ? "All brands"
                                                    : assignedBrands.map((bid) => brandLabel(bid)).join(" + ")}
                                            </span>
                                        </span>
                                        <span>
                                            <span className="text-neutral-400">Status · </span>
                                            <span className="text-ink">{u.active === false ? "Inactive" : "Active"}</span>
                                        </span>
                                    </div>
                                </div>

                                {!isOwnerRow && (
                                    <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-mist">
                                        <select
                                            value={u.role}
                                            onChange={(e) => updateUser(u.id, { role: e.target.value })}
                                            disabled={isMe}
                                            data-testid={`user-role-${u.email}`}
                                            className={`${inp} w-auto py-1.5`}
                                        >
                                            {ASSIGNABLE_ROLES.map((r) => (
                                                <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                            {/* preserve legacy role if present so the dropdown stays consistent */}
                                            {![ROLE.ADMIN_SALES, ROLE.ADMIN_INPUT, ROLE.FINANCE, ROLE.SUPERVISOR, ROLE.META_ADS_VIEWER].includes(u.role) && (
                                                <option value={u.role}>{roleLabel(u.role)}</option>
                                            )}
                                        </select>
                                        <div
                                            className="flex flex-wrap gap-1.5"
                                            data-testid={`user-brands-${u.email}`}
                                        >
                                            {brands.map((b) => {
                                                const checked = assignedBrands.includes(b.id);
                                                const canMulti = MULTI_BRAND_ROLES.includes(u.role);
                                                const disabled = isMe || (!canMulti && checked);
                                                return (
                                                    <button
                                                        key={b.id}
                                                        type="button"
                                                        disabled={disabled}
                                                        data-testid={`user-brand-toggle-${u.email}-${b.id}`}
                                                        onClick={() => {
                                                            const cur = assignedBrands;
                                                            let next;
                                                            if (canMulti) {
                                                                next = checked
                                                                    ? cur.filter((x) => x !== b.id)
                                                                    : [...cur, b.id];
                                                                if (!next.length) return;
                                                            } else {
                                                                next = [b.id];
                                                            }
                                                            updateUser(u.id, { brands: next });
                                                        }}
                                                        className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
                                                            checked
                                                                ? "bg-sage-dark text-white border-sage-dark"
                                                                : "border-mist text-neutral-500 hover:border-sage-dark"
                                                        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
                                                    >
                                                        {b.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="eyebrow">Active</span>
                                            <Switch
                                                checked={u.active !== false}
                                                onCheckedChange={(v) => updateUser(u.id, { active: v })}
                                                disabled={isMe}
                                                data-testid={`user-active-${u.email}`}
                                            />
                                        </div>
                                        <button
                                            onClick={() => setResetFor(u)}
                                            data-testid={`user-reset-${u.email}`}
                                            className="inline-flex items-center gap-1 border border-mist px-3 py-1.5 rounded-sm text-xs uppercase tracking-widest hover:border-sage-dark"
                                        >
                                            <Key size={12} weight="light" /> Reset password
                                        </button>
                                        <button
                                            onClick={() => setDeleteFor(u)}
                                            data-testid={`user-delete-${u.email}`}
                                            disabled={isMe}
                                            title="Permanently delete this user"
                                            aria-label={`Delete ${u.name}`}
                                            className="ml-auto inline-flex items-center gap-1 text-neutral-400 hover:text-red-600 text-xs uppercase tracking-widest px-2 py-1.5 rounded-sm disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <Trash size={12} weight="light" />
                                            <span className="hidden md:inline">Delete</span>
                                        </button>
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            <p className="text-xs text-neutral-500 mt-3">
                Deactivated users remain for history. The Owner account cannot be deleted. Owners always inherit access to every brand.
            </p>

            {showAdd && <AddUserModal onClose={() => setShowAdd(false)} onCreated={load} brands={brands} />}
            {resetFor && <ResetPasswordModal user={resetFor} onClose={() => setResetFor(null)} />}
            {deleteFor && (
                <DeleteUserModal
                    user={deleteFor}
                    onClose={() => setDeleteFor(null)}
                    onDeleted={load}
                />
            )}
        </div>
    );
}

function AddUserModal({ onClose, onCreated, brands }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        role: ROLE.ADMIN_SALES,
        brands: ["hoshizora"],
        password: "",
        active: true,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const toggleBrand = (bid) => {
        setForm((f) => {
            const has = f.brands.includes(bid);
            const next = has ? f.brands.filter((x) => x !== bid) : [...f.brands, bid];
            return { ...f, brands: next.length ? next : f.brands };
        });
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await api.post("/users", {
                name: form.name,
                email: form.email,
                role: form.role,
                brands: form.brands,
                brand: form.brands[0],
                password: form.password,
                active: form.active,
            });
            toast.success("User created");
            onCreated();
            onClose();
        } catch (ex) {
            setErr(formatApiError(ex?.response?.data?.detail) || "Failed to create");
        } finally {
            setBusy(false);
        }
    };

    return (
        <ModalShell title="Add User" onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="add-user-form">
                <Field label="Name">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} data-testid="add-user-name" />
                </Field>
                <Field label="Email">
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`${inp} font-mono`} data-testid="add-user-email" />
                </Field>
                <Field label="Role">
                    <select
                        value={form.role}
                        onChange={(e) => setForm({ ...form, role: e.target.value })}
                        className={inp}
                        data-testid="add-user-role"
                    >
                        {ASSIGNABLE_ROLES.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Brand Access">
                    <div className="flex flex-wrap gap-1.5">
                        {brands.map((b) => {
                            const checked = form.brands.includes(b.id);
                            return (
                                <button
                                    key={b.id}
                                    type="button"
                                    onClick={() => toggleBrand(b.id)}
                                    data-testid={`add-user-brand-${b.id}`}
                                    className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-widest border transition-colors ${
                                        checked
                                            ? "bg-sage-dark text-white border-sage-dark"
                                            : "border-mist text-neutral-500 hover:border-sage-dark"
                                    }`}
                                >
                                    {b.name}
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-[11px] text-neutral-500 mt-1">
                        Select one or both brands. Multi-brand users get a brand switcher in the header.
                    </p>
                </Field>
                <Field label="Temporary Password">
                    <input required type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={`${inp} font-mono`} data-testid="add-user-password" />
                    <p className="text-xs text-neutral-500 mt-1">
                        Share this with the user; they can change it from Settings.
                    </p>
                </Field>
                <div className="flex items-center gap-2 pt-1">
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="add-user-active" />
                    <span className="text-sm">Active immediately</span>
                </div>
                {err && <div className="text-sm text-red-600" data-testid="add-user-error">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="add-user-submit" className="inline-flex items-center gap-1 bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                        <Check size={12} weight="bold" /> {busy ? "Creating…" : "Create"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function ResetPasswordModal({ user, onClose }) {
    const [pw, setPw] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr("");
        try {
            await api.post(`/users/${user.id}/reset-password`, { new_password: pw });
            toast.success("Password reset");
            onClose();
        } catch (ex) {
            setErr(formatApiError(ex?.response?.data?.detail) || "Failed");
        } finally { setBusy(false); }
    };
    return (
        <ModalShell title={`Reset Password · ${user.name}`} onClose={onClose}>
            <form onSubmit={submit} className="space-y-3" data-testid="reset-password-form">
                <Field label="New Password">
                    <input required type="text" value={pw} onChange={(e) => setPw(e.target.value)} className={`${inp} font-mono`} data-testid="reset-pw-input" />
                </Field>
                {err && <div className="text-sm text-red-600">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose} className="border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest">Cancel</button>
                    <button type="submit" disabled={busy} data-testid="reset-pw-submit" className="bg-sage-dark text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-sage-deep disabled:opacity-60">
                        {busy ? "Saving…" : "Reset"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function DeleteUserModal({ user, onClose, onDeleted }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const submit = async () => {
        setBusy(true);
        setErr("");
        try {
            await api.delete(`/users/${user.id}`);
            toast.success("User deleted");
            onDeleted();
            onClose();
        } catch (ex) {
            setErr(formatApiError(ex?.response?.data?.detail) || "Failed");
        } finally {
            setBusy(false);
        }
    };
    return (
        <ModalShell title="Delete this user?" onClose={onClose}>
            <div className="space-y-4" data-testid="delete-user-modal">
                <p className="text-sm text-neutral-600 leading-relaxed">
                    This will permanently remove{" "}
                    <span className="font-medium text-ink">{user.name}</span> ({user.email}) and
                    their login access. This action cannot be undone.
                </p>
                <p className="text-xs text-neutral-500 leading-relaxed">
                    Any products, categories, or data they created remain intact — only the user
                    account is removed.
                </p>
                {err && <div className="text-sm text-red-600">{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="delete-user-cancel"
                        className="border border-mist px-4 py-2 rounded-sm text-xs uppercase tracking-widest"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={busy}
                        data-testid="delete-user-confirm"
                        className="inline-flex items-center gap-1 bg-red-600 text-white px-4 py-2 rounded-sm text-xs uppercase tracking-widest hover:bg-red-700 disabled:opacity-60"
                    >
                        <Trash size={12} weight="light" /> {busy ? "Deleting…" : "Delete User"}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

function ModalShell({ title, onClose, children }) {
    // Lock background scroll only while the modal is open (iOS Safari fires a
    // tiny bounce on body-scroll even inside a `fixed` overlay otherwise).
    // We never lock scrolling inside the modal itself — see overflow-y-auto
    // on the body region below.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);
    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
            style={{
                // dvh matches the live viewport on iOS Safari when the URL bar
                // collapses/expands; svh is a safer fallback inside the stack.
                paddingTop: "max(1rem, env(safe-area-inset-top))",
                paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
            }}
        >
            <div
                className="bg-ivory w-full max-w-md rounded-sm border border-mist flex flex-col"
                style={{ maxHeight: "100dvh" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between p-5 pb-3 shrink-0">
                    <h2 className="font-serif text-xl">{title}</h2>
                    <button onClick={onClose} className="text-neutral-500 hover:text-ink" aria-label="Close">
                        <X size={16} weight="light" />
                    </button>
                </div>
                <div className="px-5 pb-5 overflow-y-auto overscroll-contain" data-testid="modal-body">
                    {children}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="eyebrow">{label}</span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
